import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import {
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Tooltip,
  Typography
} from 'antd'
import axios from 'axios'
import Const from 'constants/constant'
import dayjs from 'dayjs'
import useDirectory from 'hooks/useDirectory'
import useMutation from 'hooks/useMutation'
import { Branch } from 'models/Branch'
import { Building, BuildingFormField } from 'models/Building'
import { Organization } from 'models/User'
import ModalBranch from 'pages/building/listBuilding/components/ModalBranch'
import useTourUploadImage, {
  TourUploadImagePath
} from 'pages/building/tourViewerPage/hook/useTourUploadImage'
import Quill from 'quill'
import useSWR from 'swr'
import { SelectBranch } from 'types/listUser'
import Utils from 'utils'
import CommonFunction from 'utils/commonFunction'
import Resizer from 'utils/imageResizer'
import Validators from 'utils/validators'

import Button from 'components/atoms/Button'
import FormItem from 'components/atoms/FormItem'
import Modal from 'components/atoms/Modal'
import Spinner from 'components/atoms/Spinner'
import CalendarIcon from 'components/icons/Calendar'
import DropdownIconCustom from 'components/icons/DropdownIconCustom'

import ImageUpload from '../ImageUpload'

import SelectOrganization from './SelectOrganization'

import 'quill/dist/quill.snow.css'

type Props = {
  handleCancel: () => void
}

export default function ModalEditBuildingDetail({ handleCancel }: Props) {
  const { t } = useTranslation()
  const { id } = useParams()
  const [form] = Form.useForm()

  const stopPropagation = useRef<boolean>(false)

  const [openModalBranch, setOpenModalBranch] = useState(false)
  const [listServices, setListServices] = useState<Branch[]>([])
  const constructionPeriodStart = Form.useWatch(
    BuildingFormField.CONSTRUCTION_PERIOD_START,
    form
  )
  const constructionPeriodEnd = Form.useWatch(
    BuildingFormField.CONSTRUCTION_PERIOD_END,
    form
  )
  const [zipcodeField, setZipcodeField] = useState<string>()
  const [defaultBranch, setDefaultBranch] = useState<SelectBranch | undefined>()
  const [selectedOrganization, setSelectedOrganization] = useState<
    Organization | undefined
  >()

  const { navigate, getBuiildingType, getDirectory } = useDirectory()
  const isTourDomain = getDirectory() === 'tour'

  const {
    data: building,
    mutate: refetchBuilding,
    isValidating
  } = useSWR<Building>(
    {
      url: `/v1/buildings/by-type?id=${id}&building_type=${getBuiildingType()}`
    },
    {
      revalidateOnMount: true
    }
  )
  function sortServicesByBranch(services: Branch[], branchName?: string) {
    switch (branchName) {
      case '関西支店': {
        const sorted = [...services]

        const targetIndex = sorted.findIndex(
          (item) => item.serviceStoreName === '和歌山営業所'
        )

        const dobokuIndex = sorted.findIndex(
          (item) => item.serviceStoreName === '土木部'
        )

        if (targetIndex !== -1 && dobokuIndex !== -1) {
          const [targetItem] = sorted.splice(targetIndex, 1)
          sorted.splice(dobokuIndex, 0, targetItem)
        }

        return sorted
      }

      case '横浜支店': {
        const result = [...services]

        const kenchikuIndex = result.findIndex(
          (item) => item.serviceStoreName === '建築部'
        )
        if (kenchikuIndex === -1) return result

        const firstIndex = result.findIndex(
          (item) => item.serviceStoreName === '建築第一部'
        )
        const secondIndex = result.findIndex(
          (item) => item.serviceStoreName === '建築第二部'
        )

        if (firstIndex === -1 && secondIndex === -1) return result

        const itemsToMove = []
        if (firstIndex !== -1) {
          itemsToMove.push(result[firstIndex])
          result.splice(firstIndex, 1)
        }
        if (secondIndex !== -1) {
          itemsToMove.push(result[secondIndex])
          result.splice(secondIndex, 1)
        }

        const newKenchikuIndex = result.findIndex(
          (item) => item.serviceStoreName === '建築部'
        )
        result.splice(newKenchikuIndex + 1, 0, ...itemsToMove)

        return result
      }

      default:
        return services
    }
  }

  useSWR(
    defaultBranch
      ? {
          url: '/v1/branches',
          params: {
            type: 'service',
            keyword: defaultBranch.name
          }
        }
      : null,
    {
      onSuccess: (services) => {
        const sortedServices = sortServicesByBranch(
          services.edges,
          defaultBranch?.name
        )

        setListServices(sortedServices)
        const existingService = services.edges.find(
          (item: any) => item.id === defaultBranch?.id
        )

        form.setFieldValue(
          BuildingFormField.BRANCH_ID,
          existingService ? existingService.id : sortedServices[0].id
        )
      }
    }
  )

  const { trigger: editBuilding, isMutating } = useMutation(
    `/v1/buildings/${id}`,
    {
      onError: (error) => {
        if (error?.response?.data?.error?.key === 'code_is_unique') {
          Utils.handleError(error, t, form)
          return
        }
        Utils.handleError(error, t, form)
        navigate('/')
      }
    }
  )

  const { uploadFileDescription: uploadLogo, isUploading: isUploadingLogo } =
    useTourUploadImage({
      buildingId: Number(id),
      path: TourUploadImagePath.LOGO
    })

  const {
    uploadFileDescription: uploadBusinessImage,
    isUploading: isUploadingBusinessImage
  } = useTourUploadImage({
    buildingId: Number(id),
    path: TourUploadImagePath.BUSINESS
  })

  const {
    uploadFileDescription: uploadTripodImage,
    isUploading: isUploadingTripodImage
  } = useTourUploadImage({
    buildingId: Number(id),
    path: TourUploadImagePath.TRIPOD
  })

  const {
    uploadFileDescription: uploadLocation,
    isUploading: isUploadingLocation
  } = useTourUploadImage({
    buildingId: Number(id),
    path: TourUploadImagePath.LOCATION
  })

  // ===== LOCATION SETTING =====
  const locationValue = Form.useWatch(BuildingFormField.LOCATION, form)
  const [embedLocationUrl, setEmbedLocationUrl] = useState<string>('')

  // ===== SUBMIT STATE =====
  const [isSubmitting, setIsSubmitting] = useState(false)

  // ===== LOGO SETTING =====
  const logoInputRef = useRef<HTMLInputElement>(null)
  const [logoThumbnail, setLogoThumbnail] = useState<string>('')

  // ===== BUSINESS SETTING =====
  const businessInputRef = useRef<HTMLInputElement>(null)
  const [businessLogoImage, setBusinessLogoImage] = useState<string>('')
  const quillRef = useRef<HTMLDivElement>(null)
  const quillInstanceRef = useRef<Quill | null>(null)

  const [initialDescriptionContent, setInitialDescriptionContent] =
    useState<string>('')

  // ===== TRIPOD SETTING =====
  const tripodInputRef = useRef<HTMLInputElement>(null)
  const [tripodImage, setTripodImage] = useState<string>('')

  // ===== LOCATION SETTING =====
  const [initialLocationContent, setInitialLocationContent] =
    useState<string>('')

  // ===== LOCATION SETTING: Load initial data =====
  // Force re-fetch building data on mount to ensure fresh data (e.g. if updated from other modals)
  useEffect(() => {
    refetchBuilding()
  }, [])

  useEffect(() => {
    const fetchInitialData = async () => {
      // If building is not loaded, do nothing
      if (!building) return

      const initialEmbedUrl = building?.extraInfo?.locationEmbed || ''
      const initialLocationUrl = building?.extraInfo?.locationUrl || ''

      // Always sync state, even if empty to handle deletion case
      setEmbedLocationUrl(initialEmbedUrl)

      if (initialLocationUrl) {
        const s3Domain = process.env.REACT_APP_S3_DOMAIN
        const isS3Link = s3Domain && initialLocationUrl.startsWith(s3Domain)

        let content = initialLocationUrl
        if (isS3Link) {
          try {
            const response = await fetch(initialLocationUrl)
            const blob = await response.blob()
            content = await blob.text()
          } catch (error) {
            console.error('Failed to fetch location from S3:', error)
          }
        }
        setInitialLocationContent(content)
        form.setFieldsValue({ [BuildingFormField.LOCATION]: content })
      } else {
        // Clear if server has no location
        // This handles the case where data was deleted on server (e.g. from another modal)
        setInitialLocationContent('')
        form.setFieldsValue({ [BuildingFormField.LOCATION]: '' })
      }
    }

    fetchInitialData()
  }, [building, form])

  // ===== LOGO SETTING: Load initial data =====
  useEffect(() => {
    if (building?.extraInfo) {
      setLogoThumbnail(building.extraInfo.logoUrl || '')
      form.setFieldsValue({
        [BuildingFormField.LOGO_LINK]: building.extraInfo.logoLink || ''
      })
    }
  }, [building, form])

  // ===== BUSINESS SETTING: Initialize Quill =====
  useEffect(() => {
    if (quillRef.current && !quillInstanceRef.current) {
      quillInstanceRef.current = new Quill(quillRef.current, {
        theme: 'snow',
        placeholder: '入力してください',
        modules: Const.QUILL_MODULES_PRIMARY,
        formats: Const.QUILL_FORMATS_PRIMARY
      })

      quillInstanceRef.current.on('text-change', () => {
        const content = quillInstanceRef.current?.root.innerHTML || ''
        form.setFieldsValue({
          [BuildingFormField.BUSINESS_DESCRIPTION]: content
        })
      })
    }
  }, [form])

  // ===== BUSINESS SETTING: Load initial data =====
  useEffect(() => {
    const loadInitialData = async () => {
      if (building?.extraInfo) {
        // Helper to update Business Image
        setBusinessLogoImage(building.extraInfo.businessImageUrl || '')

        // Helper to update Description
        if (building.extraInfo.descriptionUrl) {
          const descUrl = building.extraInfo.descriptionUrl
          // If URL hasn't changed from what we loaded, we might skip fetching if we wanted to optmize,
          // but strictly for sync correctness, fetching is safer unless valid URL same.
          // For now, fetch always to be safe against content updates at same URL.
          let content = ''
          try {
            const response = await fetch(descUrl)
            const blob = await response.blob()
            content = await blob.text()
          } catch (error) {
            content = descUrl
          }

          if (quillInstanceRef.current) {
            const currentContent = quillInstanceRef.current.root.innerHTML
            // Update editor only if content is substantially different to avoid cursor jumps if user typing (though here we rely on building prop update)
            if (currentContent !== content) {
              quillInstanceRef.current.root.innerHTML = content
            }
          }
          setInitialDescriptionContent(content)
          form.setFieldsValue({
            [BuildingFormField.BUSINESS_DESCRIPTION]: content
          })
        } else {
          // Clear if no description URL (meaning deleted)
          if (quillInstanceRef.current) {
            quillInstanceRef.current.root.innerHTML = ''
          }
          setInitialDescriptionContent('')
          form.setFieldsValue({
            [BuildingFormField.BUSINESS_DESCRIPTION]: ''
          })
        }
      }
    }

    loadInitialData()
  }, [building, form])

  // ===== TRIPOD SETTING: Load initial data =====
  useEffect(() => {
    if (building?.extraInfo) {
      setTripodImage(building.extraInfo.tripodImageUrl || '')
    }
  }, [building])

  // ===== LOGO HANDLERS =====
  const handleLogoChooseFile = () => {
    logoInputRef.current?.click()
  }

  const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      form.setFieldsValue({ [BuildingFormField.LOGO]: file })
      const reader = new FileReader()
      reader.onload = (event) => {
        setLogoThumbnail(event.target?.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  // ===== BUSINESS HANDLERS =====
  const handleBusinessChooseFile = () => {
    businessInputRef.current?.click()
  }

  const handleBusinessFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      form.setFieldsValue({ [BuildingFormField.BUSINESS_IMAGE]: file })
      const reader = new FileReader()
      reader.onload = (event) => {
        setBusinessLogoImage(event.target?.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  // ===== TRIPOD HANDLERS =====
  const handleTripodChooseFile = () => {
    tripodInputRef.current?.click()
  }

  const handleTripodFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      form.setFieldsValue({ [BuildingFormField.TRIPOD_IMAGE]: file })
      const reader = new FileReader()
      reader.onload = (event) => {
        setTripodImage(event.target?.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  // Get embed URL for iframe preview
  const iframeSrc =
    locationValue && locationValue !== initialLocationContent
      ? CommonFunction.getEmbedUrl(locationValue)
      : CommonFunction.getEmbedUrl(embedLocationUrl) ||
        CommonFunction.getEmbedUrl(locationValue)

  const openSelectBranch = () => {
    setOpenModalBranch(true)
  }
  const handleChangedZipcode = (e: React.ChangeEvent<HTMLInputElement>) => {
    setZipcodeField(e.target.value)
  }

  const handleGetCodeAddress = async () => {
    try {
      const res = await axios.get(
        `https://zipcloud.ibsnet.co.jp/api/search?zipcode=${
          zipcodeField?.trim() || building?.zipcode
        }`
      )
      const address = res.data.results[0]
      form.setFieldsValue({
        [BuildingFormField.PREF_CODE]: Number(address.prefcode),
        [BuildingFormField.CITY_NAME]: address.address2,
        [BuildingFormField.STREET_NAME]: address.address3
      })
    } catch (error) {
      // TODO
    }
  }

  const handleEditInformation = async (values: any) => {
    setIsSubmitting(true)

    const convertedValues = Utils.buildingFormDataConverted(
      Utils.trimObjectValues(values)
    )

    // Upload extra_info files to S3 first (only if there are actual changes)
    let logoKey: string | undefined
    let businessImageKey: string | undefined
    let tripodImageKey: string | undefined
    let descriptionKey: string | undefined
    let locationKey: string | undefined

    try {
      // Only upload tripodImage if user selected a new file (Available for all domains)
      if (convertedValues.tripodImage instanceof File) {
        tripodImageKey = await uploadTripodImage(convertedValues.tripodImage)
      }

      if (isTourDomain) {
        // Only upload logo if user selected a new file
        if (convertedValues.logo instanceof File) {
          logoKey = await uploadLogo(convertedValues.logo)
        }
        // Only upload businessImage if user selected a new file
        if (convertedValues.businessImage instanceof File) {
          businessImageKey = await uploadBusinessImage(
            convertedValues.businessImage
          )
        }

        // Note: businessDescription and location uploads are handled separately

        // Handle business description upload
        // Get content directly from Quill editor (not from form values, as there's no Form.Item for it)
        const descriptionContent =
          quillInstanceRef.current?.root.innerHTML || ''

        // Only upload if content exists AND changed from initial

        const isDescriptionChanged =
          descriptionContent !== initialDescriptionContent

        if (isDescriptionChanged) {
          const descriptionFile = await Resizer.ResizedQuillImages(
            descriptionContent
          )
          const blob = new Blob([descriptionFile], { type: 'text/plain' })
          const file = new File([blob], 'business_description.txt', {
            type: 'text/plain',
            lastModified: new Date().getTime()
          })
          descriptionKey = await uploadBusinessImage(file)
        }

        // Handle location upload
        const locationContent = convertedValues.location

        // Only upload if content exists AND changed from initial
        const isLocationChanged =
          locationContent?.trim() !== initialLocationContent?.trim()
        if (locationContent && isLocationChanged) {
          const blob = new Blob([locationContent], { type: 'text/plain' })
          const file = new File([blob], 'location.txt', {
            type: 'text/plain',
            lastModified: new Date().getTime()
          })
          locationKey = await uploadLocation(file)
        }
      }
    } catch (error) {
      console.error('Failed to upload files to S3:', error)
      setIsSubmitting(false)
      return
    }

    const formData = new FormData()

    Object.keys(convertedValues).forEach((key) => {
      // Override for Logo: use uploaded S3 key if available
      if (key === BuildingFormField.LOGO) {
        if (logoKey && convertedValues.logo instanceof File) {
          formData.append(BuildingFormField.LOGO, logoKey)
        }
        return
      }
      // Override for Business Image: use uploaded S3 key if available
      if (key === BuildingFormField.BUSINESS_IMAGE) {
        if (businessImageKey && convertedValues.businessImage instanceof File) {
          formData.append(BuildingFormField.BUSINESS_IMAGE, businessImageKey)
        }
        return
      }

      // Override for Tripod Image: use uploaded S3 key if available
      if (key === BuildingFormField.TRIPOD_IMAGE) {
        if (tripodImageKey && convertedValues.tripodImage instanceof File) {
          formData.append(BuildingFormField.TRIPOD_IMAGE, tripodImageKey)
        }
        return
      }

      // Override for Location
      if (key === BuildingFormField.LOCATION) {
        if (locationKey) {
          formData.append(BuildingFormField.LOCATION, locationKey)
        }
        // If no location (removed url), send empty string
        else if (isTourDomain && convertedValues[key] === undefined) {
          formData.append(BuildingFormField.LOCATION, '')
        }
        return
      }

      // LogoLink: only send if changed from initial value
      if (key === BuildingFormField.LOGO_LINK) {
        const initialLogoLink = building?.extraInfo?.logoLink || ''
        const currentVal = convertedValues.logoLink || ''
        if (currentVal !== initialLogoLink) {
          formData.append(BuildingFormField.LOGO_LINK, currentVal)
        }
        return
      }

      if (key === BuildingFormField.IMAGE) {
        if (convertedValues[key] instanceof File) {
          formData.append(key, convertedValues[key])
        }
        return
      }
      if (
        (key === BuildingFormField.CONSTRUCTION_PERIOD_END ||
          key === BuildingFormField.CONSTRUCTION_PERIOD_START) &&
        !convertedValues[key]
      ) {
        return
      }
      if (
        key === BuildingFormField.BUILDING_AREA ||
        key === BuildingFormField.TOTAL_FLOOR_AREA ||
        key === BuildingFormField.SITE_AREA
      ) {
        formData.append(key, convertedValues[key])
        return
      }
      if (key === BuildingFormField.ORGANIZATION_ID && !convertedValues[key]) {
        if (building?.organization?.id) {
          formData.append(
            BuildingFormField.ORGANIZATION_ID,
            `${building?.organization.id}`
          )
        }
        return
      }
      if (key === BuildingFormField.PREF_CODE && !convertedValues[key]) {
        return
      }

      // Original default append logic
      formData.append(key, convertedValues[key] ? convertedValues[key] : '')
    })

    // Append description separately (not in convertedValues because no Form.Item for it)
    if (descriptionKey) {
      formData.append('description', descriptionKey)
    }

    const isUploading =
      isUploadingLogo ||
      isUploadingBusinessImage ||
      isUploadingTripodImage ||
      isUploadingLocation

    if (isMutating || isUploading) {
      setIsSubmitting(false)
      return
    }

    editBuilding(
      {
        method: 'patch',
        form,
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' },
        successMessage: t('building.saved')
      },
      {
        onSuccess: () => {
          setIsSubmitting(false)
          refetchBuilding()
          handleCancel()
        },
        onError: () => {
          setIsSubmitting(false)
        }
      }
    )
  }

  const handleSelectBranch = async (value: SelectBranch) => {
    setOpenModalBranch(false)
    setDefaultBranch(value)
  }

  const disabledStartDate = (currentDate: any) => {
    return (
      constructionPeriodEnd &&
      dayjs(currentDate).format('YYYY-MM-DD') >=
        dayjs(constructionPeriodEnd).format('YYYY-MM-DD')
    )
  }

  const disabledEndDate = (currentDate: any) => {
    return (
      constructionPeriodStart &&
      dayjs(currentDate).format('YYYY-MM-DD') <=
        dayjs(constructionPeriodStart).format('YYYY-MM-DD')
    )
  }

  const handleEnterSubmit = () => {
    if (stopPropagation.current || openModalBranch) {
      return
    }
    form
      .validateFields()
      .then(() => handleEditInformation(form.getFieldsValue()))
      .catch(() => {})
  }

  useEffect(() => {
    if (building) {
      form.setFieldsValue({
        ...building,
        [BuildingFormField.ORGANIZATION_ID]: building?.organization?.id,
        [BuildingFormField.S3_PATH]: building?.thumbnail || building?.s3Path,
        [BuildingFormField.LOGO]: building?.extraInfo?.logoUrl, // Set initial value for logo
        [BuildingFormField.BUSINESS_IMAGE]:
          building?.extraInfo?.businessImageUrl, // Set initial value for businessImage
        [BuildingFormField.TRIPOD_IMAGE]: building?.extraInfo?.tripodImageUrl, // Set initial value for tripodImage
        [BuildingFormField.CONSTRUCTION_PERIOD_START]:
          building?.constructionPeriodStart &&
          dayjs(building.constructionPeriodStart),
        [BuildingFormField.CONSTRUCTION_PERIOD_END]:
          building?.constructionPeriodEnd &&
          dayjs(building.constructionPeriodEnd)
      })

      setDefaultBranch(building.branch)
      setSelectedOrganization(building.organization)
    }
  }, [building, form])

  return (
    <>
      <Modal
        width={690}
        className="edit-building-modal"
        zIndex={999}
        isEnterable
        onEnter={handleEnterSubmit}
        bodyStyle={{
          height: 'calc(100vh - 80px)'
        }}
      >
        <Spinner spinning={isMutating || isValidating || isSubmitting}>
          <Form
            form={form}
            colon={false}
            onFinish={handleEditInformation}
            labelAlign="left"
            onFocus={() => {
              stopPropagation.current = true
            }}
            onBlur={() => {
              stopPropagation.current = false
            }}
          >
            <Typography className="text-xl leading-[30px] font-bold text-center theme-text-primary">
              {t('building.editInformation')}
            </Typography>
            <div className="border-0 border-b border-solid theme-border-primary w-10 m-auto mt-[10px] mb-[14px]" />

            <div className="text-center m-auto w-[660px] px-[93.5px] max-h-[calc(100vh-230px)] overflow-y-auto">
              <Typography className="leading-[23.17px] font-[700] theme-text-primary text-left mb-[14px]">
                {t('building.propertyInformation')}
              </Typography>

              {/* field: name */}
              <Row justify="space-between">
                <Col className="h-[38px] flex items-center">
                  <Typography className="text-[14px] leading-[21px] font-bold text-[#021120]">
                    {t('building.buildingNameForm')}
                  </Typography>
                </Col>
                <Col>
                  <Form.Item
                    name={BuildingFormField.NAME}
                    className="thin-error-message thin-light-placeholder"
                    rules={[
                      {
                        validator: Validators.nameBuildingValidator(t)
                      }
                    ]}
                  >
                    <Input
                      className="w-[340px] h-[38px] text-sm text-[#021120]"
                      placeholder={t('common.floor.placeHolder')}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* field: code */}
              <Row justify="space-between">
                <Col className="h-[38px] flex items-center">
                  <Typography className="text-[14px] leading-[21px] font-bold text-[#021120]">
                    {t('building.code')}
                  </Typography>
                </Col>
                <Col>
                  <Form.Item
                    name={BuildingFormField.CODE}
                    className="thin-error-message thin-light-placeholder"
                    rules={[
                      {
                        validator: Validators.codeValidator(t)
                      }
                    ]}
                  >
                    <Input
                      className="w-[340px] h-[38px] text-sm text-[#021120]"
                      placeholder={t('common.floor.placeHolder')}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* field: aliasName */}
              <Row justify="space-between">
                <Col className="h-[38px] flex items-center">
                  <Typography className="text-[14px] leading-[21px] font-bold text-[#021120]">
                    {t('building.constructionName')}
                  </Typography>
                </Col>
                <Col>
                  <Form.Item
                    name={BuildingFormField.ALIAS_NAME}
                    className="thin-error-message thin-light-placeholder"
                    rules={[
                      {
                        validator: Validators.aliasNameValidator(t)
                      }
                    ]}
                  >
                    <Input
                      className="w-[340px] h-[38px] text-sm text-[#021120]"
                      placeholder={t('common.floor.placeHolder')}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* field: zipcode */}
              <Row justify="space-between">
                <Col className="h-[38px] flex items-center">
                  <Typography className="text-[14px] leading-[21px] font-bold text-[#021120]">
                    {t('building.postCode')}
                  </Typography>
                </Col>
                <Col className="flex gap-[19px]">
                  <Form.Item
                    name={BuildingFormField.ZIPCODE}
                    className="thin-error-message thin-light-placeholder"
                    rules={[
                      { validator: Validators.zipcodeValidator(t) },
                      {
                        transform: (value: string) => value.trim()
                      }
                    ]}
                  >
                    <Input
                      className="w-[220px] h-[38px] text-sm text-[#021120]"
                      placeholder={t('common.floor.placeHolder')}
                      onChange={handleChangedZipcode}
                    />
                  </Form.Item>
                  <div className="bg-[#F9FBFF]">
                    <Button
                      sizing="w-[100px] h-[40px]"
                      type="primary"
                      onClick={handleGetCodeAddress}
                    >
                      {t('organization.buttonZipCode')}
                    </Button>
                  </div>
                </Col>
              </Row>

              {/* field: prefCode */}
              <Row justify="space-between">
                <Col className="h-[38px] flex items-center">
                  <Typography className="text-[14px] leading-[21px] font-bold text-[#021120]">
                    {t('building.province')}
                  </Typography>
                </Col>
                <Col>
                  <Form.Item
                    name={BuildingFormField.PREF_CODE}
                    className="thin-light-placeholder thin-error-message"
                  >
                    <Select
                      className="!w-[340px] input-edit-information "
                      placeholder={t('translation.selectProvinceCode')}
                      suffixIcon={<DropdownIconCustom colorIcon="#021120" />}
                    >
                      {Const.PREFECTURES.map((children: any) => {
                        return (
                          <Select.Option
                            key={children.prefCode}
                            value={children.prefCode}
                          >
                            <span className="text-sm text-[#021120]">
                              {children.prefName}
                            </span>
                          </Select.Option>
                        )
                      })}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              {/* field: cityName */}
              <Row justify="space-between">
                <Col className="h-[38px] flex items-center">
                  <Typography className="text-[14px] leading-[21px] font-bold text-[#021120]">
                    {t('building.city')}
                  </Typography>
                </Col>
                <Col>
                  <Form.Item
                    name={BuildingFormField.CITY_NAME}
                    className="thin-error-message thin-light-placeholder"
                    rules={[
                      {
                        validator: Validators.keywordsAndLengthCustomValidator(
                          t,
                          20,
                          false,
                          true
                        )
                      }
                    ]}
                  >
                    <Input
                      className="w-[340px] h-[38px] text-sm text-[#021120]"
                      placeholder={t('common.floor.placeHolder')}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* field: streetName */}
              <Row justify="space-between">
                <Col className="h-[38px] flex items-center">
                  <Typography className="text-[14px] leading-[21px] font-bold text-[#021120]">
                    {t('building.street')}
                  </Typography>
                </Col>
                <Col>
                  <Form.Item
                    name={BuildingFormField.STREET_NAME}
                    className="thin-error-message thin-light-placeholder"
                    rules={[
                      {
                        validator: Validators.keywordsAndLengthCustomValidator(
                          t,
                          30,
                          false,
                          true
                        )
                      }
                    ]}
                  >
                    <Input
                      className="w-[340px] h-[38px] text-sm text-[#021120]"
                      placeholder={t('common.floor.placeHolder')}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* field: buildingName */}
              <Row justify="space-between">
                <Col className="h-[38px] flex items-center">
                  <Typography className="text-[14px] leading-[21px] font-bold text-[#021120]">
                    {t('building.apartment')}
                  </Typography>
                </Col>
                <Col>
                  <Form.Item
                    name={BuildingFormField.BUILDING_NAME}
                    className="thin-error-message thin-light-placeholder"
                    rules={[
                      {
                        validator: Validators.keywordsAndLengthCustomValidator(
                          t,
                          50,
                          false,
                          true
                        )
                      }
                    ]}
                  >
                    <Input
                      className="w-[340px] h-[38px] text-sm text-[#021120]"
                      placeholder={t('common.floor.placeHolder')}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* field: branch select */}
              <Row justify="space-between">
                <Col className="h-[38px] flex items-center">
                  <Typography className="text-[14px] leading-[21px] font-bold text-[#021120]">
                    {t('building.branchName')}
                  </Typography>
                </Col>
                <Col>
                  <Form.Item className="input-invite-user add-building mb-6">
                    <div className="ml-auto flex w-[340px] justify-between items-center">
                      <div className="text-[14px] leading-[21px] font-medium text-[#021120]">
                        {defaultBranch?.name}
                      </div>
                      <Button
                        type="primary"
                        sizing="w-[100px] h-10"
                        className="ml-14"
                        onClick={openSelectBranch}
                      >
                        {t('user.buttonSelectOrganization')}
                      </Button>
                    </div>
                  </Form.Item>
                </Col>
              </Row>

              {/* field: branchId */}
              {defaultBranch && (
                <Row justify="space-between">
                  <Col />
                  <Col className="w-[340px]">
                    <Row justify="space-between">
                      <Col className="h-[38px] flex items-center">
                        <Typography className="text-[14px] leading-[21px] font-bold text-[#021120]">
                          {t('building.serviceName')}
                        </Typography>
                      </Col>
                      <Col>
                        <FormItem
                          wrapperCol={{ span: 24 }}
                          name={BuildingFormField.BRANCH_ID}
                          className="flex justify-end mb-8"
                          line={false}
                        >
                          <Select
                            className="!w-[278px] float-right rounded-md text-[14px] leading-[21px] font-medium input-edit-information"
                            placeholder={t('building.placeholder')}
                            suffixIcon={
                              <DropdownIconCustom colorIcon="#000000" />
                            }
                          >
                            {listServices.map((service) => {
                              return (
                                <Select.Option
                                  value={service.id}
                                  key={service.id}
                                >
                                  <span className="text-sm text-[#021120]">
                                    {service.serviceStoreName}
                                  </span>
                                </Select.Option>
                              )
                            })}
                          </Select>
                        </FormItem>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              )}

              {/* field: organizationId */}
              <SelectOrganization
                form={form}
                selectedOrganization={selectedOrganization}
                setSelectedOrganization={setSelectedOrganization}
              />

              {/* field: image */}
              <Row justify="space-between">
                <Col className="h-[120px] flex items-center">
                  <Typography className="text-[14px] leading-[21px] font-bold text-[#021120]">
                    {t('building.propertyImage')}
                  </Typography>
                </Col>
                <Col>
                  <ImageUpload form={form} isValidating={isValidating} />
                </Col>
              </Row>

              <div className="w-full mb-[30px] border-0 border-b border-[#CED0D4] border-solid" />

              <Typography className="text-[16px] leading-[23.17px] font-[700] theme-text-primary text-left mb-[14px]">
                {t('building.propertyData')}
              </Typography>

              {/* field: siteArea */}
              <Row justify="space-between">
                <Col className="h-[38px] flex items-center">
                  <Typography className="text-[14px] leading-[21px] font-medium text-[#021120]">
                    {t('building.siteAreaSquare')}
                  </Typography>
                </Col>
                <Col className="flex">
                  <Form.Item
                    name={BuildingFormField.SITE_AREA}
                    className="thin-error-message thin-light-placeholder"
                    rules={[
                      {
                        validator: Validators.inputAreaValidator(t)
                      }
                    ]}
                  >
                    <Input
                      className="w-[310px] h-[38px] text-sm text-[#021120]"
                      placeholder={t('common.floor.placeHolder')}
                    />
                  </Form.Item>
                  <div className="h-[38px] flex">
                    <span className="px-2 mt-auto text-[14px] leading-[21px] font-medium text-[#021120]">
                      &#13217;
                    </span>
                  </div>
                </Col>
              </Row>

              {/* field: buildingArea */}
              <Row justify="space-between">
                <Col className="h-[38px] flex items-center">
                  <Typography className="text-[14px] leading-[21px] font-medium text-[#021120]">
                    {t('building.buildingAreaSquare')}
                  </Typography>
                </Col>
                <Col className="flex">
                  <Form.Item
                    name={BuildingFormField.BUILDING_AREA}
                    className="thin-error-message thin-light-placeholder"
                    rules={[
                      {
                        validator: Validators.inputAreaValidator(t)
                      }
                    ]}
                  >
                    <Input
                      className="w-[310px] h-[38px] text-sm text-[#021120]"
                      placeholder={t('common.floor.placeHolder')}
                    />
                  </Form.Item>
                  <div className="h-[38px] flex">
                    <span className="px-2 mt-auto text-[14px] leading-[21px] font-medium text-[#021120]">
                      &#13217;
                    </span>
                  </div>
                </Col>
              </Row>

              {/* field: totalFloorArea */}
              <Row justify="space-between">
                <Col className="h-[38px] flex items-center">
                  <Typography className="text-[14px] leading-[21px] font-medium text-[#021120]">
                    {t('building.totalFloorAreaSquare')}
                  </Typography>
                </Col>
                <Col className="flex">
                  <Form.Item
                    name={BuildingFormField.TOTAL_FLOOR_AREA}
                    className="thin-error-message thin-light-placeholder"
                    rules={[
                      {
                        validator: Validators.inputAreaValidator(t)
                      }
                    ]}
                  >
                    <Input
                      className="w-[310px] h-[38px] text-sm text-[#021120]"
                      placeholder={t('common.floor.placeHolder')}
                    />
                  </Form.Item>
                  <div className="h-[38px] flex">
                    <span className="px-2 mt-auto text-[14px] leading-[21px] font-medium text-[#021120]">
                      &#13217;
                    </span>
                  </div>
                </Col>
              </Row>

              {/* field: structure */}
              <Row justify="space-between">
                <Col className="h-[38px] flex items-center">
                  <Typography className="text-[14px] leading-[21px] font-medium text-[#021120]">
                    {t('building.architectureStructure')}
                  </Typography>
                </Col>
                <Col>
                  <Form.Item
                    name={BuildingFormField.STRUCTURE}
                    className="thin-error-message thin-light-placeholder"
                    rules={[
                      {
                        validator: Validators.keywordsAndLengthCustomValidator(
                          t,
                          200,
                          undefined,
                          true
                        )
                      }
                    ]}
                  >
                    <Input
                      className="w-[310px] h-[38px] text-sm mr-[30px] text-[#021120]"
                      placeholder={t('common.floor.placeHolder')}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* field: floorNumber */}
              <Row justify="space-between">
                <Col className="h-[38px] flex items-center">
                  <Typography className="text-[14px] leading-[21px] font-medium text-[#021120]">
                    {t('building.numberOfFloor')}
                  </Typography>
                </Col>
                <Col>
                  <Form.Item
                    name={BuildingFormField.FLOOR_NUMBER}
                    className="thin-error-message thin-light-placeholder"
                    rules={[
                      {
                        validator: Validators.keywordsAndLengthCustomValidator(
                          t,
                          200,
                          false,
                          true
                        )
                      }
                    ]}
                  >
                    <Input
                      className="w-[310px] h-[38px] text-sm mr-[30px] text-[#021120]"
                      placeholder={t('common.floor.placeHolder')}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <div className="w-full mb-[30px] border-0 border-b border-[#CED0D4] border-solid" />

              <Typography className="text-[16px] leading-[23.16px] font-[700] theme-text-primary text-left mb-[14px]">
                {t('building.designConstruction')}
              </Typography>

              {/* field: designerName */}
              <Row justify="space-between">
                <Col className="h-[38px] flex items-center">
                  <Typography className="text-[14px] leading-[21px] font-medium text-[#021120]">
                    {t('building.design')}
                  </Typography>
                </Col>
                <Col>
                  <Form.Item
                    name={BuildingFormField.DESIGNER_NAME}
                    className="thin-error-message thin-light-placeholder"
                    rules={[
                      {
                        validator: Validators.keywordsAndLengthCustomValidator(
                          t,
                          200,
                          false,
                          true
                        )
                      }
                    ]}
                  >
                    <Input
                      className="w-[340px] h-[38px] text-sm text-[#021120]"
                      placeholder={t('common.floor.placeHolder')}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* field: architectName */}
              <Row justify="space-between">
                <Col className="h-[38px] flex items-center">
                  <Typography className="text-[14px] leading-[21px] font-medium text-[#021120]">
                    {t('building.construction')}
                  </Typography>
                </Col>
                <Col>
                  <Form.Item
                    name={BuildingFormField.ARCHITECT_NAME}
                    className="thin-error-message thin-light-placeholder"
                    rules={[
                      {
                        validator: Validators.keywordsAndLengthCustomValidator(
                          t,
                          200,
                          false,
                          true
                        )
                      }
                    ]}
                  >
                    <Input
                      className="w-[340px] h-[38px] text-sm text-[#021120]"
                      placeholder={t('common.floor.placeHolder')}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* field: constructionPeriodStart & constructionPeriodEnd */}
              <Row justify="space-between">
                <Col className="h-[38px] flex items-center">
                  <Typography className="text-[14px] leading-[21px] font-medium text-[#021120]">
                    {t('building.constructionPeriod')}
                  </Typography>
                </Col>
                <Col>
                  <Form.Item className="!mb-0">
                    <div className="grid grid-cols-[1fr_auto_1fr] w-[340px]">
                      <Form.Item
                        name={BuildingFormField.CONSTRUCTION_PERIOD_START}
                        className="mb-0"
                      >
                        <DatePicker
                          placeholder=""
                          className="h-[38px] text-sm"
                          suffixIcon={<CalendarIcon />}
                          format="YYYY年MM月DD日"
                          disabledDate={disabledStartDate}
                        />
                      </Form.Item>
                      <span className="flex items-center h-[38px] p-[10px] text-[#021120]">
                        〜
                      </span>
                      <Form.Item
                        name={BuildingFormField.CONSTRUCTION_PERIOD_END}
                        className="mb-0"
                      >
                        <DatePicker
                          placeholder=""
                          className="h-[38px] !text-sm"
                          suffixIcon={<CalendarIcon />}
                          format="YYYY年MM月DD日"
                          disabledDate={disabledEndDate}
                        />
                      </Form.Item>
                    </div>
                  </Form.Item>
                </Col>
              </Row>

              {isTourDomain && (
                <>
                  {/* ===== LOCATION SETTING SECTION ===== */}
                  <div className="w-full mb-[30px] border-0 border-b border-[#CED0D4] border-solid" />

                  <Typography className="text-[16px] leading-[23.17px] font-[700] theme-text-primary text-left mb-[14px]">
                    {t('building.mapSetting.title')}
                  </Typography>

                  {/* Map url */}
                  <Form.Item
                    name={BuildingFormField.LOCATION}
                    className="input-floor thin-error-message"
                    label={t('building.mapSetting.googleMapUrl')}
                    rules={[
                      {
                        validator: Validators.urlValidatorForIframeGoogleMap(t)
                      }
                    ]}
                  >
                    <Input
                      className="w-full h-[38px] text-[14px] font-[400]"
                      placeholder={t(
                        'building.mapSetting.googleMapUrl_PlaceHolder'
                      )}
                    />
                  </Form.Item>

                  {/* Frame map */}
                  <div className="mb-[30px]">
                    <div className="flex justify-center items-center">
                      {iframeSrc ? (
                        <iframe
                          src={iframeSrc}
                          className="w-[450px] h-[274px] pb-4"
                          style={{ border: 0 }}
                          allowFullScreen
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                          title="Google Map"
                        />
                      ) : (
                        <div className="w-full h-[auto] flex flex-col items-center justify-center">
                          <p className="text-[14px] font-[500] text-gray-700 mb-3">
                            GoogleマップURLの取得方法
                          </p>
                          <img
                            src="/assets/get-gg-map-iframe-tutorial.gif"
                            alt="Guide"
                            className="w-full h-full object-contain rounded-lg bg-gray-100 border border-dashed border-gray-300"
                          />
                        </div>
                      )}
                    </div>
                    {locationValue && (
                      <div
                        className="underline text-center text-blue-500 cursor-pointer"
                        onClick={() => {
                          window.open(iframeSrc || locationValue, '_blank')
                        }}
                      >
                        {t('building.mapSetting.openMap')}
                      </div>
                    )}
                  </div>

                  {/* ===== LOGO SETTING SECTION ===== */}
                  <div className="w-full mb-[30px] border-0 border-b border-[#CED0D4] border-solid" />
                  <Typography className="text-[16px] leading-[23.17px] font-[700] theme-text-primary text-left mb-4">
                    {t('building.logoSetting.title')}
                  </Typography>

                  <div className="flex justify-between items-center gap-x-4 mb-[20px]">
                    <Typography.Text className="font-bold">
                      {t('building.imageLabel')}
                    </Typography.Text>

                    <Form.Item
                      name={BuildingFormField.LOGO}
                      className="logo-upload !mb-0"
                      style={{ flex: 1, minWidth: 0 }}
                    >
                      <div
                        onClick={handleLogoChooseFile}
                        className="ml-4 w-full overflow-hidden text-center cursor-pointer"
                      >
                        {logoThumbnail ? (
                          <img
                            src={logoThumbnail}
                            className="max-h-[118px] max-w-[300px] object-contain inline-block bg-[#021120]"
                            alt="logo"
                          />
                        ) : (
                          <Typography.Text>
                            {t('building.noImageNote')}
                          </Typography.Text>
                        )}
                      </div>
                    </Form.Item>

                    <div className="flex flex-col gap-y-[2px] items-center">
                      <div className="flex flex-col justify-center items-center">
                        <Button
                          type="primary"
                          onClick={handleLogoChooseFile}
                          className="mt-2"
                          sizing="w-[120px] h-[40px]"
                        >
                          {t('building.chooseLogoFileBtn')}
                        </Button>
                        <div className="text-[10px] font-[400px] pt-2">
                          {t('building.fileSizeNote')}
                        </div>
                      </div>
                      <input
                        type="file"
                        ref={logoInputRef}
                        onChange={handleLogoFileChange}
                        hidden
                        accept=".jpg,.jpeg,.png,.JPG,.JPEG,.PNG"
                      />
                    </div>
                  </div>

                  <Row justify="space-between" className="mb-[30px]">
                    <Col className="h-[38px] flex items-center">
                      <Typography className="text-[14px] leading-[21px] font-medium text-[#021120]">
                        {t('building.logoSetting.logoLink')}
                      </Typography>
                    </Col>
                    <Col>
                      <Form.Item
                        name={BuildingFormField.LOGO_LINK}
                        className="thin-error-message thin-light-placeholder"
                        rules={[
                          {
                            validator: Validators.urlValidator(t)
                          }
                        ]}
                      >
                        <Input
                          className="w-[340px] h-[38px] text-sm text-[#021120]"
                          placeholder={t('building.logoSetting.placeholder')}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* ===== BUSINESS SETTING SECTION ===== */}
                  <div className="w-full mb-[30px] border-0 border-b border-[#CED0D4] border-solid" />
                  <Typography className="text-[16px] leading-[23.17px] font-[700] theme-text-primary text-left mb-4">
                    {t('building.businessSetting.title')}
                  </Typography>

                  <div className="flex justify-between items-center gap-x-4 mb-[20px]">
                    <Typography.Text className="font-bold">
                      {t('building.businessSetting.image_title')}
                    </Typography.Text>

                    <Form.Item
                      name={BuildingFormField.BUSINESS_IMAGE}
                      className="logo-upload !mb-0"
                      style={{ flex: 1, minWidth: 0 }}
                    >
                      <div
                        onClick={handleBusinessChooseFile}
                        className="ml-4 w-full overflow-hidden text-center cursor-pointer"
                      >
                        {businessLogoImage ? (
                          <img
                            src={businessLogoImage}
                            className="max-h-[118px] max-w-[300px] object-contain inline-block bg-[#021120]"
                            alt="business"
                          />
                        ) : (
                          <Typography.Text>
                            {t('building.noImageNote')}
                          </Typography.Text>
                        )}
                      </div>
                    </Form.Item>

                    <div className="flex flex-col gap-y-[2px] items-center">
                      <div className="flex flex-col justify-center items-center">
                        <Button
                          type="primary"
                          onClick={handleBusinessChooseFile}
                          className="mt-2"
                          sizing="w-[120px] h-[40px]"
                        >
                          {t('building.chooseLogoFileBtn')}
                        </Button>
                        <div className="text-[10px] font-[400px] pt-2">
                          {t('building.fileSizeNote')}
                        </div>
                      </div>
                      <input
                        type="file"
                        ref={businessInputRef}
                        onChange={handleBusinessFileChange}
                        hidden
                        accept=".jpg,.jpeg,.png,.JPG,.JPEG,.PNG"
                      />
                    </div>
                  </div>

                  <div className="h-[300px] mb-[80px]">
                    <div
                      ref={quillRef}
                      className="quill-custom-editor overflow-hidden [&>#editor-resizer>.toolbar]:!hidden [&>.ql-editor]:pb-10"
                    />
                  </div>
                </>
              )}
              {/* ===== TRIPOD SETTING SECTION ===== */}
              <div className="w-full mb-[30px] border-0 border-b border-[#CED0D4] border-solid" />
              <Typography className="text-[16px] leading-[23.17px] font-[700] theme-text-primary text-left mb-4">
                {t('building.tripodSetting.title')}
              </Typography>

              {/* Image Selection Row */}
              <div className="flex justify-between items-center gap-x-4">
                <Typography.Text className="font-bold">
                  {t('building.easerTripod.image_title')}
                </Typography.Text>

                {/* Show selected file name */}
                <Form.Item
                  name="tripodImageWrapper"
                  dependencies={[BuildingFormField.TRIPOD_IMAGE]}
                  hasFeedback
                  className="logo-upload !mb-0"
                  style={{ flex: 1, minWidth: 0 }}
                >
                  <div
                    onClick={handleTripodChooseFile}
                    className="ml-4 w-full overflow-hidden text-ellipsis whitespace-nowrap text-center cursor-pointer"
                  >
                    {form.getFieldValue(
                      BuildingFormField.TRIPOD_IMAGE
                    ) instanceof File ? (
                      <Tooltip
                        title={
                          form.getFieldValue(BuildingFormField.TRIPOD_IMAGE)
                            ?.name || t('building.noImageNote')
                        }
                        placement="topLeft"
                      >
                        <Typography.Text className="truncate text-center w-full">
                          {
                            form.getFieldValue(BuildingFormField.TRIPOD_IMAGE)
                              ?.name
                          }
                        </Typography.Text>
                      </Tooltip>
                    ) : (
                      <Typography.Text className="text-center w-full">
                        {t('building.noImageNote')}
                      </Typography.Text>
                    )}
                  </div>
                </Form.Item>

                <div className="flex flex-col gap-y-[2px] items-center">
                  <div className="flex flex-col justify-center items-center">
                    <Button
                      type="primary"
                      onClick={handleTripodChooseFile}
                      className="mt-2"
                      sizing="w-[120px] h-[40px]"
                    >
                      {t('building.chooseLogoFileBtn')}
                    </Button>
                    <div className="text-[10px] font-[400px] pt-2">
                      {t('building.fileSizeNote')}
                    </div>
                  </div>

                  <Form.Item
                    name={BuildingFormField.TRIPOD_IMAGE}
                    className="mb-0 hidden"
                    rules={[
                      {
                        validator: Validators.validateFileSize(t, 50)
                      }
                    ]}
                  >
                    <div className="hidden" />
                  </Form.Item>

                  <input
                    accept=".jpg,.jpeg,.png,.JPG,.JPEG,.PNG"
                    type="file"
                    ref={tripodInputRef}
                    onChange={handleTripodFileChange}
                    hidden
                  />
                </div>
              </div>

              {/* Note */}
              <Typography.Text className="text-sm font-[400px] mt-4 block text-left">
                {t('building.easerTripod.note')}
              </Typography.Text>

              {/* Image Preview */}
              <div className="relative w-full h-[236px] flex items-center justify-center mb-[30px]">
                {tripodImage && (
                  <div className="relative z-10">
                    <img
                      src={tripodImage}
                      alt="tripodImage"
                      className="object-contain max-h-[200px] max-w-[400px]"
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-10 justify-center w-full items-end h-full py-5 border-0 border-t border-solid border-[#EEEFF2]">
              <Button
                sizing="w-[140px] h-[50px]"
                className="bg-[#A6AAAF]"
                onClick={() => {
                  handleCancel()
                  form.resetFields()
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                sizing="w-[140px] h-[50px]"
                type="primary"
                htmlType="submit"
              >
                {t('common.confirm')}
              </Button>
            </div>
          </Form>
        </Spinner>
      </Modal>

      {openModalBranch && (
        <ModalBranch
          handleCancel={() => setOpenModalBranch(false)}
          handleOk={handleSelectBranch}
          buildingBranchName={defaultBranch?.name}
        />
      )}
    </>
  )
}
